import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetKidOverviewHistoricalData } from '../../../../../api/kids/hooks/queries';
import { RaidrV2RoutePaths } from '../../../../../routes/page-routes';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { cn } from '../../../../../utilities/cn';
import { FaChevronDown } from 'react-icons/fa';
import { KidDocumentStatus, KidShareClass } from '../types/KidEditorV2.types';
import { zIndexes } from '../../../../../styling/zIndexes';

interface CompareVersionSelectorProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  shareClass: KidShareClass & { fundID: string };
  clientId: string;
  fundId: string;
  /** Current target version from URL (without "v" prefix) — pre-selects Target (left) when opening the popover */
  initialTargetVersion?: string;
  /** Current compare version from URL (without "v" prefix) — pre-selects Comparison (right) when opening the popover */
  initialCompareVersion?: string;
}

// --------------------------------------------------------------------------
// Inner panel — all selection state lives here so it resets on close/unmount
// --------------------------------------------------------------------------

interface VersionSelectorContentProps {
  groupedVersions: Record<string, string[]>;
  isLoading: boolean;
  shareClass: KidShareClass & { fundID: string };
  close: () => void;
  initialTargetVersion?: string;
  initialCompareVersion?: string;
}

const VersionSelectorContent: FunctionComponent<VersionSelectorContentProps> = ({
  groupedVersions,
  isLoading,
  shareClass,
  close,
  initialTargetVersion,
  initialCompareVersion,
}) => {
  const navigate = useNavigate();
  const [compareVersion, setCompareVersion] = useState<string | null>(
    initialCompareVersion && initialCompareVersion !== '-' ? initialCompareVersion : null,
  );
  const [targetVersion, setTargetVersion] = useState<string | null>(
    initialTargetVersion && initialTargetVersion !== '-' ? initialTargetVersion : null,
  );
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [groupsInitialized, setGroupsInitialized] = useState(false);

  const leftScrollRef = useRef<HTMLDivElement>(null);
  const rightScrollRef = useRef<HTMLDivElement>(null);
  const isSyncingRef = useRef(false);

  const handleLeftScroll = () => {
    if (isSyncingRef.current || !rightScrollRef.current || !leftScrollRef.current) return;
    isSyncingRef.current = true;
    rightScrollRef.current.scrollTop = leftScrollRef.current.scrollTop;
    isSyncingRef.current = false;
  };

  const handleRightScroll = () => {
    if (isSyncingRef.current || !leftScrollRef.current || !rightScrollRef.current) return;
    isSyncingRef.current = true;
    leftScrollRef.current.scrollTop = rightScrollRef.current.scrollTop;
    isSyncingRef.current = false;
  };

  // Expand only the latest major version by default once data is available.
  // Also expand any major version that contains the currently selected versions so they're visible.
  useEffect(() => {
    if (groupsInitialized) return;
    const majorVersions = Object.keys(groupedVersions).sort((a, b) => Number(b) - Number(a));
    if (majorVersions.length === 0) return;
    const toExpand = new Set<string>([majorVersions[0]]);
    if (targetVersion) {
      const major = targetVersion.split('.')[0];
      toExpand.add(major);
    }
    if (compareVersion) {
      const major = compareVersion.split('.')[0];
      toExpand.add(major);
    }
    setExpandedGroups(toExpand);
    setGroupsInitialized(true);
  }, [groupedVersions, groupsInitialized, targetVersion, compareVersion]);

  const handleView = () => {
    if (!compareVersion || !targetVersion) return;
    const params = new URLSearchParams({
      fundId: shareClass.fundID,
      shareClassId: shareClass.share_class_id,
      documentLanguage: shareClass.document_language || 'English',
      compareVersion: compareVersion.replace(/^v/, ''),
      targetVersion: targetVersion.replace(/^v/, ''),
    });
    close();
    navigate(`${RaidrV2RoutePaths.KID_COMPARE_V2}?${params.toString()}`);
  };

  const toggleGroup = (majorVersion: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      next.has(majorVersion) ? next.delete(majorVersion) : next.add(majorVersion);
      return next;
    });
  };

  const renderVersionList = (
    versions: Record<string, string[]>,
    selectedVersion: string | null,
    disabledVersion: string | null,
    onSelect: (version: string) => void,
  ) => {
    if (isLoading) {
      return (
        <div className="space-y-3" role="status" aria-label="Loading versions">
          {[1, 2, 3].map((groupIndex) => (
            <div key={groupIndex} className="space-y-1">
              <div className="px-2">
                <div className="h-3 w-12 animate-pulse rounded bg-gray-200" />
              </div>
              {[1, 2, 3].map((itemIndex) => (
                <div
                  key={itemIndex}
                  className="flex items-center space-x-2 rounded px-2 py-1.5 pl-6"
                >
                  <div className="h-4 w-4 animate-pulse rounded border border-gray-200 bg-gray-100" />
                  <div className="h-3 w-16 animate-pulse rounded bg-gray-200" />
                </div>
              ))}
            </div>
          ))}
          <span className="sr-only">Loading versions...</span>
        </div>
      );
    }

    const majorVersions = Object.keys(versions).sort((a, b) => Number(b) - Number(a));

    if (majorVersions.length === 0) {
      return (
        <div className="py-4 text-center text-xs text-gray-500">
          No versions available
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {majorVersions.map((majorVersion) => {
          const versionList = versions[majorVersion];
          const isExpanded = expandedGroups.has(majorVersion);
          const versionsToShow = isExpanded ? versionList : [];

          return (
            <div key={majorVersion} className="space-y-1">
              <button
                onClick={() => toggleGroup(majorVersion)}
                className="flex w-full items-center justify-between border-b border-gray-200 px-2 pb-2 text-xs font-semibold text-gray-500 hover:text-gray-700"
              >
                <span>
                  v{majorVersion}
                  <span className="ml-1.5 font-normal text-gray-400">
                    ({versionList.length} version{versionList.length === 1 ? '' : 's'})
                  </span>
                </span>
                <FaChevronDown
                  className={`h-3 w-3 text-blue-600 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                />
              </button>
              {versionsToShow.map((version) => {
                const disabled = disabledVersion === version;
                const selected = selectedVersion === version;
                return (
                  <button
                    key={version}
                    onClick={() => onSelect(version)}
                    disabled={disabled}
                    className={`flex w-full items-center space-x-2 rounded px-2 py-1.5 pl-2 text-left text-xs transition-colors ${selected ? 'bg-blue-100 font-medium text-gray-700' : 'text-gray-700 hover:bg-gray-50'
                      } ${disabled ? 'cursor-not-allowed opacity-40' : ''}`}
                  >
                    <div
                      className={`flex h-4 w-4 items-center justify-center rounded border ${selected ? 'border-blue-600 bg-blue-600' : 'border-gray-300'
                        }`}
                    >
                      {selected && (
                        <svg
                          className="h-3 w-3 text-white"
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <span>v{version}</span>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="p-3">
      <div className="mb-3 border-b border-gray-200 pb-2 text-sm font-semibold text-mainblue">
        Compare Versions
      </div>

      {/* Fixed column headers */}
      <div className="mb-1 grid grid-cols-2 gap-4 border-b border-gray-100 pb-2">
        <div className="text-xs font-semibold text-mainblue">Target (left)</div>
        <div className="text-xs font-semibold text-mainblue">Comparison (right)</div>
      </div>

      {/* Fixed-height scrollable version lists — both columns scroll together */}
      <div className="grid h-40 grid-cols-2 gap-4 overflow-hidden">
        <div
          ref={leftScrollRef}
          onScroll={handleLeftScroll}
          className="overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {renderVersionList(groupedVersions, targetVersion, compareVersion, setTargetVersion)}
        </div>
        <div
          ref={rightScrollRef}
          onScroll={handleRightScroll}
          className="scrollbar-custom overflow-y-auto pr-1"
        >
          {renderVersionList(groupedVersions, compareVersion, targetVersion, setCompareVersion)}
        </div>
      </div>

      {/* Fixed footer */}
      <div className="mt-3 border-t border-gray-200 pt-3">
        <div className="mb-2 text-xs font-semibold text-mainblue">
          Selected: {targetVersion ? `v${targetVersion}` : '—'} ↔{' '}
          {compareVersion ? `v${compareVersion}` : '—'}
        </div>
        <button
          onClick={handleView}
          disabled={!(compareVersion && targetVersion && compareVersion !== targetVersion)}
          className="flex w-full items-center justify-center rounded-md bg-mainblue px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-mainblue/90 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:opacity-50"
        >
          View
        </button>
      </div>
    </div>
  );
};

// --------------------------------------------------------------------------
// Outer component — handles data fetching and the popover shell only
// --------------------------------------------------------------------------

const CompareVersionSelector: FunctionComponent<CompareVersionSelectorProps> = ({
  shareClass,
  clientId,
  fundId,
  initialTargetVersion,
  initialCompareVersion,
  ...props
}) => {
  const hasDocument = shareClass.status !== KidDocumentStatus.NoDocument;
  const { data: historicalData, isLoading } = useGetKidOverviewHistoricalData(
    clientId,
    fundId,
    shareClass.share_class_id,
    shareClass.document_language,
    {
      enabled:
        hasDocument &&
        !!clientId &&
        !!fundId &&
        !!shareClass.share_class_id,
    },
  );

  const groupedVersions = useMemo(() => {
    const versionSet = new Set<string>();

    if (shareClass.version_number && shareClass.version_number !== '-') {
      versionSet.add(shareClass.version_number);
    }

    if (historicalData) {
      Object.values(historicalData).forEach((items) => {
        items.forEach((item) => {
          if (item.version_number) versionSet.add(item.version_number);
        });
      });
    }

    const sortedVersions = Array.from(versionSet).sort((a, b) => {
      const aParts = a.split('.').map(Number);
      const bParts = b.split('.').map(Number);
      for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
        const diff = (bParts[i] || 0) - (aParts[i] || 0);
        if (diff !== 0) return diff;
      }
      return 0;
    });

    const grouped: Record<string, string[]> = {};
    sortedVersions.forEach((version) => {
      const major = version.split('.')[0];
      if (!grouped[major]) grouped[major] = [];
      grouped[major].push(version);
    });

    return grouped;
  }, [historicalData, shareClass.version_number]);

  return (
    <Popover className="relative">
      {({ close }) => (
        <>
          <PopoverButton
            {...props}
            className={cn(
              'w-24 rounded-md border border-gray-300 px-2 py-1 text-xs font-medium transition-colors hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500',
              props.className,
            )}
          >
            Select versions
          </PopoverButton>

          <PopoverPanel
            anchor={{ to: 'bottom', gap: 4, padding: 16 }}
            style={{ zIndex: zIndexes.floatingPanel }}
            className="w-[28rem] origin-top-left overflow-visible rounded-lg border border-gray-200 bg-white shadow-lg focus:outline-none [--anchor-max-height:none]"
          >
            {/* Mounted fresh each open → all selection state resets on close */}
            <VersionSelectorContent
              groupedVersions={groupedVersions}
              isLoading={isLoading}
              shareClass={shareClass}
              close={close}
              initialTargetVersion={initialTargetVersion}
              initialCompareVersion={initialCompareVersion}
            />
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
};

export default CompareVersionSelector;
